import { computed, DestroyRef, inject, Injector, runInInjectionContext } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { setError, setIdle, setLoading, setRefreshing, withRequestStatus } from '@app/signal-store/with-request-status';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { Events } from '@ngrx/signals/events';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { defaultIfEmpty, defer, filter, forkJoin, map, Observable, pipe, Subject } from 'rxjs';
import { exhaustMap, switchMap, takeUntil } from 'rxjs/operators';

import type { PageRequest, PendingRequestsPage } from './api/requests.models';
import { requestEvents } from './events/request.events';

export const PAGE_LIMIT = 25;

export const FIRST_PAGE = 1;

export interface SourceState<TRow> {
  rows: TRow[];
  page: number;
  hasMore: boolean;
}

export const freshSource = <TRow>(): SourceState<TRow> => ({ rows: [], page: FIRST_PAGE, hasMore: true });

export const exhaustedSource = <TRow>(): SourceState<TRow> => ({ rows: [], page: FIRST_PAGE, hasMore: false });

export const appendPage = <TRow>(
  source: SourceState<TRow>,
  page: PendingRequestsPage<TRow>,
  identify: (row: TRow) => string | undefined,
  morePagesAllowed: boolean,
): SourceState<TRow> => {
  const knownRows = new Set(source.rows.map(identify));

  return {
    rows: [...source.rows, ...page.rows.filter((row) => !knownRows.has(identify(row)))],
    page: source.page + 1,
    hasMore: morePagesAllowed && page.hasMore && page.rows.length > 0,
  };
};

const appendPages = <TKey extends string, TRow>(
  sources: Record<TKey, SourceState<TRow>>,
  pages: { key: TKey; page: PendingRequestsPage<TRow> }[],
  identify: (row: TRow) => string | undefined,
  morePagesAllowed: boolean,
): Record<TKey, SourceState<TRow>> => {
  const nextSources = { ...sources };

  for (const { key, page } of pages) {
    nextSources[key] = appendPage(sources[key], page, identify, morePagesAllowed);
  }

  return nextSources;
};

interface NextPagesFetchConfig<TKey extends string, TRow> {
  keys: TKey[];
  fetchers: PagedSourcesResources<TKey, TRow>['fetchers'];
  identify: (row: TRow) => string | undefined;
  paginationEnabled: boolean;
}

/** Fetches the next page of every source that has more, and returns the sources with them appended. */
const nextPagesFetcher =
  <TKey extends string, TRow>({ keys, fetchers, identify, paginationEnabled }: NextPagesFetchConfig<TKey, TRow>) =>
  (sources: Record<TKey, SourceState<TRow>>): Observable<Record<TKey, SourceState<TRow>>> =>
    forkJoin(
      keys
        .filter((key) => sources[key].hasMore)
        .map((key) =>
          fetchers[key](paginationEnabled ? { limit: PAGE_LIMIT, page: sources[key].page } : undefined).pipe(
            map((page) => ({ key, page })),
          ),
        ),
    ).pipe(
      defaultIfEmpty([]),
      map((pages) => appendPages(sources, pages, identify, paginationEnabled)),
    );

export interface PagedSourcesResources<TKey extends string, TRow> {
  fetchers: Record<TKey, (page?: PageRequest) => Observable<PendingRequestsPage<TRow>>>;
  paginationEnabled: boolean;
  /** Gates a load cycle on something other than pagination state, e.g. an auth id not yet known. */
  canLoad?: () => boolean;
}

export interface PagedSourcesConfig<TKey extends string, TRow> {
  keys: TKey[];
  /**
   * Builds the per-source state, fresh. Called once at construction and again on every
   * `reload()` — may safely call `inject()`, since `withPagedSources` always invokes it inside
   * an injection context (directly at construction, or via `runInInjectionContext` afterwards).
   */
  buildState: () => Record<TKey, SourceState<TRow>>;
  /** Called once, at construction, inside an injection context — inject freely here. */
  buildResources: () => PagedSourcesResources<TKey, TRow>;
  identifyRow: (row: TRow) => string | undefined;
}

/**
 * Shared pagination lifecycle for a signal store backing a requests panel: per-source paging
 * state, loading/refreshing/error status, generation-guarded fetch cancellation, and a reload
 * triggered by `requestEvents.resolved` that keeps the loaded rows until the fresh ones land.
 * `MyRequestsPanelStore` and `RequestsPanelStore` are its two consumers — the only things that
 * differ between them are which sources exist, how each source is fetched, and how a row is
 * identified for de-duplication, all supplied here.
 */
interface PagedSourcesState<TRow> {
  sources: Record<string, SourceState<TRow>>;
  generation: number;
  loadMoreFailed: boolean;
}

export function withPagedSources<TKey extends string, TRow>(config: PagedSourcesConfig<TKey, TRow>) {
  const { keys, buildState, buildResources, identifyRow } = config;

  return signalStoreFeature(
    withState<PagedSourcesState<TRow>>(() => ({ sources: buildState(), generation: 0, loadMoreFailed: false })),
    withRequestStatus(),
    withComputed(({ sources }) => ({
      hasMore: computed(() => keys.some((key) => sources()[key].hasMore)),
      rowCount: computed(() => keys.reduce((total, key) => total + sources()[key].rows.length, 0)),
    })),
    withMethods((store) => {
      const injector = inject(Injector);
      const { fetchers, paginationEnabled, canLoad = () => true } = buildResources();
      const cancelPending$ = new Subject<void>();

      const fetchNextPages = nextPagesFetcher({ keys, fetchers, identify: identifyRow, paginationEnabled });

      const loadNextPage = rxMethod<void>(
        pipe(
          filter(() => store.hasMore() && canLoad() && !store.isRefreshing()),
          exhaustMap(() => {
            const generation = store.generation();

            return defer(() => {
              patchState(store, store.rowCount() > 0 ? setRefreshing() : setLoading());

              return fetchNextPages(store.sources());
            }).pipe(
              tapResponse({
                next: (sources) => {
                  if (store.generation() !== generation) {
                    return;
                  }

                  patchState(store, { sources, loadMoreFailed: false }, setIdle());
                },
                error: () => {
                  if (store.generation() !== generation) {
                    return;
                  }

                  // A later page failing must not wipe the rows already on screen, so only an
                  // initial load with nothing to show falls back to the full-panel error state.
                  patchState(store, store.rowCount() > 0 ? { loadMoreFailed: true, ...setIdle() } : setError());
                },
              }),
              takeUntil(cancelPending$),
            );
          }),
        ),
      );

      const refresh = rxMethod<Record<TKey, SourceState<TRow>>>(
        pipe(
          switchMap((freshSources) =>
            fetchNextPages(freshSources).pipe(
              tapResponse({
                next: (sources) => patchState(store, { sources }, setIdle()),
                error: () => patchState(store, { sources: freshSources }, setError()),
              }),
              takeUntil(cancelPending$),
            ),
          ),
        ),
      );

      return {
        loadNextPage,
        reload: (): void => {
          cancelPending$.next();

          const freshSources = runInInjectionContext(injector, buildState);

          const nextCycle = { generation: store.generation() + 1, loadMoreFailed: false };

          if (store.isLoading() || store.isError() || !canLoad()) {
            patchState(store, { sources: freshSources, ...nextCycle }, setIdle());
            loadNextPage();

            return;
          }

          // What the panel shows stays until the fresh rows land, even when it shows no rows:
          // falling back to the loading state would tear down the components rendering it, and
          // with them any state the user set there, such as which department sections are open.
          patchState(store, nextCycle, setRefreshing());
          refresh(freshSources);
        },
      };
    }),
    withHooks({ onInit: (store) => useReloadOnResolvedRequest(store) }),
  );
}

const useReloadOnResolvedRequest = (store: { reload: () => void }): void => {
  inject(Events)
    .on(requestEvents.resolved)
    .pipe(takeUntilDestroyed(inject(DestroyRef)))
    .subscribe(() => store.reload());
};
